import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Input } from '@components/ui/input';
import { Textarea } from '@components/ui/textarea';
import { Label } from '@components/ui/label';
import { Button } from '@components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { CheckCircle2, ExternalLink, Loader2, ShieldCheck, Globe, Sparkles, Users, Crown, PartyPopper } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@components/ui/dialog';
import { FadeIn, ScaleIn, AnimatePresence, AnimatedList } from '@skills/motion-animations.jsx';
import GradientBackground from '@generated/components/GradientBackground';
import FormCard from '@generated/components/FormCard';
import { createRegistration } from '@generated/server/registrations';

const VALUE_PROPS = [
  {
    icon: Globe,
    title: 'Share publicly',
    desc: 'Publish apps anyone can access, no monday account needed.',
  },
  {
    icon: Sparkles,
    title: 'AI-powered builder',
    desc: 'Describe what you need and Vibe builds it in moments.',
  },
  {
    icon: Users,
    title: 'Early access',
    desc: 'Shape the future of public apps with direct feedback.',
  },
];

export default function App() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowDisclaimer(true), 600);
    return () => clearTimeout(timer);
  }, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data: any) => {
    setSubmitting(true);
    setError(null);

    try {
      await createRegistration({
        data: {
          name: data.name,
          email: data.email,
          mondayUrl: data.mondayUrl,
          useCase: data.useCase,
          whoOwnsThisAccount: data.whoOwnsThisAccount,
        },
      });

      setSubmitted(true);
    } catch (err) {
      console.error('Registration failed:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full relative">
      <GradientBackground />

      <Dialog open={showDisclaimer} onOpenChange={setShowDisclaimer}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-9 h-9 rounded-lg bg-secondary/15 flex items-center justify-center">
                <PartyPopper className="w-4.5 h-4.5 text-secondary" />
              </div>
              <DialogTitle className="font-[family-name:var(--font-heading)] text-lg font-[600]">
                Public Vibe Apps is live!
              </DialogTitle>
            </div>
            <DialogDescription className="text-[0.85rem] leading-relaxed pt-2">
              Public Vibe Apps has already been released to all accounts on the <strong className="text-foreground font-[600]">Vibe Growth</strong> tier. You can publish up to 25 apps right now — no waitlist needed.
            </DialogDescription>
          </DialogHeader>
          <p className="text-[0.8rem] text-muted-foreground leading-relaxed">
            This form helps us learn about your use case so we can follow up and support your experience. Fill it out and we'll be in touch!
          </p>
          <DialogFooter>
            <Button
              onClick={() => setShowDisclaimer(false)}
              className="w-full sm:w-auto bg-foreground text-background hover:bg-foreground/90 font-[600] text-sm"
            >
              Got it, let's go
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AnimatePresence mode="wait">
        {!submitted ? (
          <div key="form" className="relative z-10 min-h-screen flex items-center justify-center px-4 sm:px-6 xl:px-12 py-8 sm:py-12">
            <div className="w-full max-w-5xl grid grid-cols-1 xl:grid-cols-[1fr_minmax(0,420px)] gap-10 sm:gap-12 xl:gap-12 items-start xl:items-center">
              {/* Left — Branding */}
              <BrandingSide />
              {/* Right — Form */}
              <FormSide
                onSubmit={handleSubmit(onSubmit)}
                register={register}
                control={control}
                errors={errors}
                submitting={submitting}
                error={error}
              />
            </div>
          </div>
        ) : (
          <SuccessScreen key="success" />
        )}
      </AnimatePresence>
    </div>
  );
}

function BrandingSide() {
  return (
    <div className="flex flex-col items-center xl:items-start text-center xl:text-left w-full">
      {/* @ts-expect-error jsx direction type */}
      <FadeIn direction="down" distance={12} duration={0.5} once={false}>
        <img
          src="https://files-public.monday.com/euc1/2d35fdf0-0311-4ecd-934b-877fa175e963/monday vibe logo.png"
          alt="monday vibe"
          className="h-8 sm:h-9 object-contain mb-5 sm:mb-7"
        />
      </FadeIn>

      {/* @ts-expect-error jsx direction type */}
      <FadeIn direction="up" distance={16} delay={0.08} duration={0.6} once={false}>
        <h1 className="font-[family-name:var(--font-heading)] text-[clamp(2rem,6vw,3.5rem)] font-[300] tracking-[-0.03em] leading-[1.08] text-foreground mb-3">
          Public Vibe Apps
        </h1>
      </FadeIn>

      {/* @ts-expect-error jsx direction type */}
      <FadeIn direction="up" distance={12} delay={0.14} duration={0.5} once={false}>
        <span className="inline-block px-3 py-1 rounded-full text-[0.65rem] font-[700] tracking-[0.08em] uppercase bg-foreground text-background mb-4 sm:mb-5">
          Beta
        </span>
      </FadeIn>

      {/* @ts-expect-error jsx direction type */}
      <FadeIn direction="up" distance={12} delay={0.2} duration={0.5} once={false}>
        <p className="text-muted-foreground text-[0.9rem] sm:text-[0.95rem] leading-relaxed max-w-sm mx-auto xl:mx-0 mb-4 sm:mb-5 px-2 sm:px-0">
          Register for early access to build and publish public apps
          on monday.com — powered by Vibe's AI app builder.
        </p>
      </FadeIn>

      {/* @ts-expect-error jsx direction type */}
      <FadeIn direction="up" distance={10} delay={0.26} duration={0.5} once={false}>
        <div className="w-full max-w-sm mx-auto xl:mx-0 mb-6 sm:mb-8 rounded-xl border border-border/80 bg-card/60 backdrop-blur-sm px-4 py-3.5">
          <div className="flex items-center gap-2 mb-1.5">
            <Crown className="w-3.5 h-3.5 text-secondary" />
            <span className="text-[0.75rem] font-[700] tracking-[0.04em] uppercase text-secondary">Vibe Growth plan</span>
          </div>
          <p className="text-[0.8rem] text-muted-foreground leading-snug text-left">
            Public Vibe Apps is already available to all accounts on the Vibe Growth tier — you can publish up to 25 apps today. Register here so we can follow up and support your experience.
          </p>
        </div>
      </FadeIn>

      {/* Value props */}
      <div className="w-full max-w-sm mx-auto xl:mx-0">
        <AnimatedList stagger={0.1} animation="fadeUp" distance={14} once={false} className="" style={{}}>
          {VALUE_PROPS.map((prop) => (
            <div key={prop.title} className="flex items-start gap-3.5 mb-4 sm:mb-5 last:mb-0">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-accent flex items-center justify-center mt-0.5">
                <prop.icon className="w-4 h-4 text-accent-foreground" />
              </div>
              <div className="text-left">
                <p className="text-sm font-[600] text-foreground mb-0.5">{prop.title}</p>
                <p className="text-[0.8rem] text-muted-foreground leading-snug">{prop.desc}</p>
              </div>
            </div>
          ))}
        </AnimatedList>
      </div>
    </div>
  );
}

function FormSide({ onSubmit, register, control, errors, submitting, error }: any) {
  return (
    // @ts-expect-error jsx direction type
    <FadeIn direction="up" distance={24} delay={0.18} duration={0.7} once={false}>
      <FormCard>
        <form onSubmit={onSubmit} className="space-y-5">
          <FormField label="Full name" id="name" error={errors.name}>
            <Input
              id="name"
              placeholder="Jane Doe"
              {...register('name', { required: 'Name is required' })}
              aria-invalid={!!errors.name}
              className="h-10 bg-[hsl(var(--surface-form))] border-transparent focus:border-border focus:bg-card text-sm transition-colors"
            />
          </FormField>

          <FormField label="Email" id="email" error={errors.email}>
            <Input
              id="email"
              type="email"
              placeholder="jane@company.com"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: 'Enter a valid email address',
                },
              })}
              aria-invalid={!!errors.email}
              className="h-10 bg-[hsl(var(--surface-form))] border-transparent focus:border-border focus:bg-card text-sm transition-colors"
            />
          </FormField>

          <FormField label="Monday account URL" id="mondayUrl" error={errors.mondayUrl}>
            <Input
              id="mondayUrl"
              type="text"
              placeholder="your-team.monday.com"
              {...register('mondayUrl', {
                required: 'Monday account URL is required',
              })}
              aria-invalid={!!errors.mondayUrl}
              className="h-10 bg-[hsl(var(--surface-form))] border-transparent focus:border-border focus:bg-card text-sm transition-colors"
            />
          </FormField>

          <FormField label="Who owns this account?" id="whoOwnsThisAccount" error={errors.whoOwnsThisAccount}>
            <Controller
              name="whoOwnsThisAccount"
              control={control}
              rules={{ required: 'Please select an option' }}
              render={({ field }) => (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger className="w-full h-10 bg-[hsl(var(--surface-form))] border-transparent focus:border-border focus:bg-card text-sm transition-colors">
                    <SelectValue placeholder="Select account type…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Partner">Partner</SelectItem>
                    <SelectItem value="Customer">Customer</SelectItem>
                    <SelectItem value="monday Employee">monday Employee</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </FormField>

          <FormField label="What do you want to build?" id="useCase" error={errors.useCase}>
            <Textarea
              id="useCase"
              rows={3}
              placeholder="Describe the app you'd like to create…"
              {...register('useCase', { required: 'Please describe your use case' })}
              aria-invalid={!!errors.useCase}
              className="bg-[hsl(var(--surface-form))] border-transparent focus:border-border focus:bg-card text-sm transition-colors resize-none"
            />
          </FormField>

          {error && (
            <div className="rounded-lg bg-destructive/8 border border-destructive/15 p-3">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 text-sm font-[600] bg-foreground text-background hover:bg-foreground/90 rounded-lg mt-1"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting…
              </>
            ) : (
              'Register for Beta'
            )}
          </Button>
        </form>

        <div className="mt-6 pt-5 border-t border-border/60">
          <a
            href="https://wkf.ms/4lRaKOz"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 text-[0.8rem] text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Share feedback on the beta experience
          </a>
        </div>
      </FormCard>

      <div className="mt-5 flex items-center justify-center gap-1.5 text-[0.75rem] text-muted-foreground/70">
        <ShieldCheck className="w-3.5 h-3.5" />
        Your data is safe with monday.com
      </div>
    </FadeIn>
  );
}

function FormField({ label, id, error, children }: any) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[0.8rem] font-[500] text-foreground/80">
        {label}
      </Label>
      {children}
      {error && (
        <p className="text-[0.75rem] text-destructive mt-0.5">{error.message}</p>
      )}
    </div>
  );
}

function SuccessScreen() {
  return (
    <div className="relative z-10 min-h-screen flex items-start lg:items-center justify-center px-4 py-8 lg:py-12">
      <ScaleIn from={0.9} delay={0} once={false} className="" style={{}}>
        <div className="w-full max-w-md mx-auto">
          <FormCard>
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-6 h-6 text-primary" />
              </div>
              <h2 className="font-[family-name:var(--font-heading)] text-xl font-[600] text-foreground mb-2">
                You're on the list
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed mb-7 max-w-xs mx-auto">
                We'll review your registration and reach out soon with next steps for building your public vibe app.
              </p>
              <a
                href="https://wkf.ms/4lRaKOz"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-foreground text-background text-sm font-[500] hover:bg-foreground/90 transition-colors duration-200"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Share your feedback
              </a>
            </div>
          </FormCard>
        </div>
      </ScaleIn>
    </div>
  );
}
